// VehicleRecognitionPopup — slides in from the right when a vehicle QR sticker
// is scanned by the USB barcode scanner connected to the Tricivo Agent.
//
// Auto-dismisses after 15 seconds. A second scan while the popup is visible
// replaces the content (the parent re-mounts with the new event, which resets
// the timer via the key prop on the countdown effect).

import { useEffect, useRef, useState } from 'react';

import type { WbVehicleRecognizedEvent } from '../../hooks/wb/useWbLiveFeed';
import styles from './VehicleRecognitionPopup.module.css';

const AUTO_DISMISS_MS = 15_000;

export interface VehicleRecognitionPopupProps {
  event: WbVehicleRecognizedEvent;
  onDismiss: () => void;
  onUseVehicle: (vehicle: NonNullable<WbVehicleRecognizedEvent['vehicle']>) => void;
}

export function VehicleRecognitionPopup({
  event,
  onDismiss,
  onUseVehicle,
}: VehicleRecognitionPopupProps): JSX.Element {
  const { source, registration, vehicle } = event;
  const [remainingMs, setRemainingMs] = useState(AUTO_DISMISS_MS);
  const startRef = useRef(Date.now());

  // Reset timer when a new event arrives (key-based reset from parent).
  useEffect(() => {
    startRef.current = Date.now();
    setRemainingMs(AUTO_DISMISS_MS);

    const interval = setInterval(() => {
      const elapsed = Date.now() - startRef.current;
      const remaining = AUTO_DISMISS_MS - elapsed;
      if (remaining <= 0) {
        clearInterval(interval);
        onDismiss();
      } else {
        setRemainingMs(remaining);
      }
    }, 200);

    return () => clearInterval(interval);
  }, [onDismiss]);

  // Dismiss on Escape key.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onDismiss();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  const progress = remainingMs / AUTO_DISMISS_MS;
  const sourceBadge = source === 'barcode' ? 'Barcode' : 'ANPR';

  return (
    <div className={styles.popup} role="dialog" aria-label="Vehicle recognised">
      <div className={styles.header}>
        <span className={styles.title}>Vehicle Recognised</span>
        <span className={styles.badge}>{sourceBadge}</span>
      </div>

      <div className={styles.body}>
        <p className={styles.registration}>{registration}</p>

        {vehicle !== null ? (
          <dl className={styles.details}>
            <dt>Type</dt>
            <dd>{capitalise(vehicle.vehicle_type)}</dd>
            {vehicle.haulier_name !== null && (
              <>
                <dt>Haulier</dt>
                <dd>{vehicle.haulier_name}</dd>
              </>
            )}
            {vehicle.stored_tare_kg !== null && (
              <>
                <dt>Stored tare</dt>
                <dd>{vehicle.stored_tare_kg.toLocaleString()} kg</dd>
              </>
            )}
          </dl>
        ) : (
          <p className={styles.notFound}>Not found in system</p>
        )}
      </div>

      <div className={styles.footer}>
        {vehicle !== null ? (
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={() => {
              onUseVehicle(vehicle);
              onDismiss();
            }}
          >
            Use this vehicle
          </button>
        ) : (
          <a
            href={`/weighbridge/vehicles/new?registration=${encodeURIComponent(registration)}`}
            className={styles.addLink}
          >
            Add vehicle
          </a>
        )}
        <button type="button" className={styles.dismissBtn} onClick={onDismiss}>
          Dismiss
        </button>
      </div>

      <div className={styles.progressTrack}>
        <div className={styles.progressBar} style={{ width: `${progress * 100}%` }} />
      </div>
    </div>
  );
}

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
